import React, { useContext, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { render as generatePlainText } from "datocms-structured-text-to-plain-text";

import GeneralPageContext from "../../templates/GeneralPageContext";

import Link from "../../components/Link";
import { DepartmentAddress } from "./DepartmentAddress/DepartmentAddress";
import { ContactText } from "./ContactText/ContactText";
import { IconTextPair } from "./IconTextPair/IconTextPair";
import { SectionContainer } from "../../components/SectionContainer";
import { ImageWithGrid } from "../../components/Image";
import { Headline } from "../../components/Headline";
import { StructuredTextRenderer } from "../../components/StructuredTextRenderer";
import { Button } from "../../components/Button";

import { generateClassString as gcs } from "../../lib/helper/generateClassString";

import {
    useIsHuge,
    useIsSmaller1400,
    useIsTablet
} from "../../lib/hooks/mediaQueries";

import PhoneIcon from "../../../static/assets/icons/icon-phone.svg";

import t from "../../lib/translate";

import * as styles from "./contactDepartment.module.less";

function ContactDepartment({
    data: {
        anker,
        background,
        ctaButton,
        department: departmentPage,
        headline,
        image,
        semanticOfHeadline,
        subheadline,
        tagline
    },
    type,
    config
}) {
    const {
        locale,
        layout: { showAnchorNavigation },
        stdPages: { pageContact }
    } = useContext(GeneralPageContext);

    const isSmaller1400 = useIsSmaller1400();
    const isHuge = useIsHuge();
    const isMobile = useIsTablet();
    const isMin1201 = useMediaQuery({ query: "(min-width: 1201px" });
    const ctaIsEmpty = useMemo(
        () => !generatePlainText(ctaButton),
        [ctaButton]
    );

    // prettier-ignore
    const gridSize = (showAnchorNavigation && isMin1201) ? isSmaller1400 ? "M" : "L" : (isMobile ? "M" : (isSmaller1400 ? "L" : (isHuge ? "XL" : "L")));

    const objectFitValue = isMobile ? "contain" : "none";

    const department = departmentPage || config.department;

    const subheader = !!generatePlainText(subheadline)
        ? subheadline
        : department.subheadline;

    const contactImage = image || department?.image;

    const filteredDivisions = department.division.filter(
        ({ phone }) => !!phone
    );

    return (
        <>
            <SectionContainer
                background={background}
                sectionId={anker}
                sectionClass={styles.removePadding}
                containerClass={gcs(styles.contactDepartmentOuterContainer, [
                    showAnchorNavigation,
                    styles.anchored
                ])}
            >
                <div className={styles.contentDepartmentContainer}>
                    <div className={styles.sectionOne}>
                        <div className={styles.imageContainer}>
                            <ImageWithGrid
                                image={contactImage}
                                gridSize={gridSize}
                                objectFit={objectFitValue}
                                objectPosition="center bottom"
                            />
                        </div>
                    </div>
                    <div className={styles.sectionTwo}>
                        <ContactText
                            tagline={tagline}
                            headline={headline || department.headline}
                            subheadline={subheader}
                            semanticOfHeadline={
                                semanticOfHeadline ||
                                department.semanticOfHeadline
                            }
                        />
                        <div className={styles.departmentTextContainer}>
                            <div className={styles.leftContainer}>
                                <div>
                                    {filteredDivisions.map(
                                        ({
                                            id,
                                            name,
                                            phone,
                                            division: {
                                                pageArea: { entryPage }
                                            }
                                        }) => (
                                            <div
                                                key={id}
                                                className={styles.phoneBlock}
                                            >
                                                <Link to={entryPage.url}>
                                                    <Headline
                                                        semanticOfHeadline="H5 Style"
                                                        headlineClass={
                                                            styles.departmentLink
                                                        }
                                                    >
                                                        {name}
                                                    </Headline>
                                                </Link>

                                                <div
                                                    className={
                                                        styles.iconTextPair
                                                    }
                                                >
                                                    <IconTextPair
                                                        contactString={phone}
                                                        icon={PhoneIcon}
                                                        type="phone"
                                                    />
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {ctaIsEmpty ? (
                                        <Button
                                            type={"cta"}
                                            renderAs="link"
                                            to={pageContact.url}
                                            icon={"mail"}
                                            iconPosition={"left"}
                                            className={styles.contactButton}
                                            aria-label={t("contact.cta.defaut.label")}
                                        >
                                            {t("contact.cta.defaut.label")}
                                        </Button>
                                    ) : (
                                        <StructuredTextRenderer
                                            data={ctaButton}
                                            itemLinkType={"cta"}
                                            itemLinkIcon={"mail"}
                                            itemLinkIconPosition={"left"}
                                            contentElementType={type}
                                            className={styles.contactButton}
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <DepartmentAddress
                                    subtitle={department.subtitle}
                                    country={department.country}
                                    name={department.name}
                                    number={department.number}
                                    place={department.place}
                                    street={department.street}
                                    zip={department.zip}
                                    locale={locale}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </SectionContainer>
        </>
    );
}

export { ContactDepartment };
